import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { SellerBalanceById } from "@/components/SellerBalance";
import { PageHeader, DataTable, StatusBadge, EmptyState, StatCard } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, FileText, CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { useLocalStorageState } from "@/hooks/useLocalStorageState";

const PAGE_SIZE = 50;
const ALL = "all"; // Radix Select forbids value="" — use a sentinel for the "no filter" option.

// KYC status -> safelisted tint classes (bg-{hue}-100 text-{hue}-800). No raw hex.
const STATUS_CLS = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};
const statusLabel = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "-");

const STATUS_FILTERS = [
  { id: ALL, label: "All" },
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

const DocLink = ({ url, label, testid }) => {
  if (!url) return <span className="text-sm text-slate-400">-</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
      data-testid={testid}
    >
      <FileText className="h-3.5 w-3.5" /> {label}
    </a>
  );
};

const KycStatusBadge = ({ status }) => (
  <StatusBadge status={status} kind="generic" label={statusLabel(status)} className={STATUS_CLS[status] || undefined} />
);

const B2CKYCQueue = ({ user, onLogout }) => {
  const [status, setStatus] = useLocalStorageState("admin-b2c-kyc:status", "pending");
  const [page, setPage] = useState(1);

  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: PAGE_SIZE, counts: {} });
  const [loading, setLoading] = useState(true);

  const [reviewTarget, setReviewTarget] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [remarksError, setRemarksError] = useState("");
  const [acting, setActing] = useState(null); // "approve" | "reject" | null

  const abortRef = useRef(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (status && status !== ALL) params.status = status;
      const { data: res } = await axios.get(`${API}/b2c/admin/kyc`, { params, signal: controller.signal });
      setData({
        items: res.items || [],
        total: res.total || 0,
        page: res.page || 1,
        page_size: res.page_size || PAGE_SIZE,
        counts: res.counts || {},
      });
    } catch (e) {
      if (axios.isCancel?.(e) || e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      toast.error(errDetail(e, "Failed to load the KYC queue"));
    } finally {
      setLoading(false);
    }
  }, [page, status]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // Reset to page 1 whenever the status filter changes.
  useEffect(() => {
    setPage(1);
  }, [status]);

  const openReview = (row) => {
    setReviewTarget(row);
    setRemarks("");
    setRemarksError("");
  };

  const closeReview = () => {
    if (acting) return;
    setReviewTarget(null);
    setRemarks("");
    setRemarksError("");
  };

  const submitDecision = async (decision) => {
    if (!reviewTarget || acting) return;
    const trimmed = remarks.trim();
    if (decision === "reject" && !trimmed) {
      setRemarksError("Remarks are required to reject a KYC submission.");
      return;
    }
    setActing(decision);
    try {
      await axios.post(`${API}/b2c/admin/kyc/${reviewTarget.seller_id}/${decision}`, {
        remarks: trimmed || undefined,
      });
      toast.success(decision === "approve" ? "KYC approved." : "KYC rejected.");
      setReviewTarget(null);
      setRemarks("");
      setRemarksError("");
      fetchQueue();
    } catch (e) {
      toast.error(errDetail(e, "Could not record the decision. Please try again."));
    } finally {
      setActing(null);
    }
  };

  const counts = data.counts || {};

  const columns = [
    {
      key: "seller",
      header: "Seller",
      primary: true,
      cell: (r) => (
        <div className="text-sm">
          <div className="font-medium text-slate-800">{r.brand_name || r.seller_id || "-"}</div>
          {r.brand_name && <div className="text-xs text-slate-500">{r.seller_id}</div>}
          <SellerBalanceById sellerId={r.seller_id} />
        </div>
      ),
    },
    {
      key: "pan",
      header: "PAN",
      cell: (r) => <span className="text-sm font-mono text-slate-700">{r.pan_number || "-"}</span>,
    },
    {
      key: "gstin",
      header: "GSTIN",
      hideOnMobile: true,
      cell: (r) =>
        r.gstin ? (
          <span className="text-sm font-mono text-slate-700">{r.gstin}</span>
        ) : (
          <span className="text-sm text-slate-400">-</span>
        ),
    },
    {
      key: "submitted",
      header: "Submitted",
      hideOnMobile: true,
      cell: (r) => (
        <div className="whitespace-nowrap text-sm text-slate-600">
          <div>{formatToIST(r.submitted_at, false)}</div>
          <div className="text-xs text-slate-500">{formatToIST(r.submitted_at).split(", ")[1]}</div>
        </div>
      ),
    },
    {
      key: "docs",
      header: "Documents",
      cell: (r) => (
        <div className="flex flex-col gap-1">
          <DocLink url={r.pan_doc_url} label="View PAN" testid={`admin-kyc-pan-doc-${r.seller_id}`} />
          <DocLink url={r.gstin_doc_url} label="View GSTIN" testid={`admin-kyc-gstin-doc-${r.seller_id}`} />
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (r) => <KycStatusBadge status={r.status} />,
    },
    {
      key: "actions",
      header: "Actions",
      isActions: true,
      cell: (r) => (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            openReview(r);
          }}
          data-testid={`admin-kyc-review-${r.seller_id}`}
        >
          <ShieldCheck className="mr-1 h-3.5 w-3.5" /> Review
        </Button>
      ),
    },
  ];

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  const emptyNode = (
    <EmptyState
      icon={ShieldCheck}
      title="No KYC submissions"
      description={
        status === "pending"
          ? "There are no submissions waiting for review right now."
          : "No KYC submissions match this filter."
      }
    />
  );

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader title="B2C KYC Queue" subtitle="Review and verify seller KYC submissions" />

        {/* Per-status summary */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard
            label="Pending"
            value={Number(counts.pending ?? 0).toLocaleString("en-IN")}
            icon={Clock}
            accent="warning"
            loading={loading}
          />
          <StatCard
            label="Approved"
            value={Number(counts.approved ?? 0).toLocaleString("en-IN")}
            icon={CheckCircle2}
            accent="success"
            loading={loading}
          />
          <StatCard
            label="Rejected"
            value={Number(counts.rejected ?? 0).toLocaleString("en-IN")}
            icon={XCircle}
            accent="danger"
            loading={loading}
          />
        </div>

        {/* Status filter */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="max-w-xs space-y-1.5">
              <Label className="text-xs text-slate-600">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="admin-kyc-filter-status">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_FILTERS.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <DataTable
          columns={columns}
          data={data.items}
          rowKey={(r) => r.seller_id}
          onRowClick={(r) => openReview(r)}
          loading={loading}
          empty={emptyNode}
          pagination={
            totalPages > 1
              ? { page, pages: totalPages, total: data.total, pageSize: PAGE_SIZE, onPageChange: setPage }
              : undefined
          }
        />
      </div>

      {/* Review dialog: remarks + approve/reject (reject requires remarks). */}
      <Dialog open={!!reviewTarget} onOpenChange={(v) => !v && closeReview()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review KYC — {reviewTarget?.brand_name || reviewTarget?.seller_id || ""}</DialogTitle>
            <DialogDescription>
              Approve to enable B2C shipping for this seller, or reject with a reason so they can resubmit.
            </DialogDescription>
          </DialogHeader>

          {reviewTarget && (
            <div className="space-y-4">
              <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                <div className="space-y-0.5">
                  <dt className="text-xs text-slate-500">PAN</dt>
                  <dd className="text-sm font-mono font-medium text-slate-800">{reviewTarget.pan_number || "-"}</dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-xs text-slate-500">PAN document</dt>
                  <dd>
                    <DocLink url={reviewTarget.pan_doc_url} label="View PAN" testid="admin-kyc-dialog-pan-doc" />
                  </dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-xs text-slate-500">GSTIN</dt>
                  <dd className="text-sm font-mono font-medium text-slate-800">
                    {reviewTarget.gstin || <span className="font-sans text-slate-400">Not provided</span>}
                  </dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-xs text-slate-500">GSTIN document</dt>
                  <dd>
                    <DocLink url={reviewTarget.gstin_doc_url} label="View GSTIN" testid="admin-kyc-dialog-gstin-doc" />
                  </dd>
                </div>
              </dl>

              {reviewTarget.bank_account && (
                <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                  <p className="mb-2 text-xs font-semibold text-slate-600">Bank account (for COD payouts)</p>
                  <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                    <div className="space-y-0.5">
                      <dt className="text-xs text-slate-500">Account holder</dt>
                      <dd className="text-sm font-medium text-slate-800">{reviewTarget.bank_account.account_holder_name || "-"}</dd>
                    </div>
                    <div className="space-y-0.5">
                      <dt className="text-xs text-slate-500">Account number</dt>
                      <dd className="text-sm font-mono font-medium text-slate-800">{reviewTarget.bank_account.account_number || "-"}</dd>
                    </div>
                    <div className="space-y-0.5">
                      <dt className="text-xs text-slate-500">IFSC</dt>
                      <dd className="text-sm font-mono font-medium text-slate-800">{reviewTarget.bank_account.ifsc || "-"}</dd>
                    </div>
                    <div className="space-y-0.5">
                      <dt className="text-xs text-slate-500">Bank</dt>
                      <dd className="text-sm font-medium text-slate-800">{reviewTarget.bank_account.bank_name || "-"}</dd>
                    </div>
                    {reviewTarget.bank_cheque_doc_url && (
                      <div className="space-y-0.5">
                        <dt className="text-xs text-slate-500">Cancelled cheque</dt>
                        <dd><DocLink url={reviewTarget.bank_cheque_doc_url} label="View cheque" testid="admin-kyc-dialog-cheque-doc" /></dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="admin-kyc-remarks" className="text-sm font-medium text-slate-700">
                  Remarks <span className="font-normal text-slate-500">(required to reject)</span>
                </Label>
                <Textarea
                  id="admin-kyc-remarks"
                  value={remarks}
                  onChange={(e) => {
                    setRemarks(e.target.value);
                    if (remarksError) setRemarksError("");
                  }}
                  placeholder="e.g. PAN image is blurry — please re-upload a clear scan."
                  rows={3}
                  data-testid="admin-kyc-remarks"
                />
                {remarksError && <p className="text-xs text-red-600">{remarksError}</p>}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={closeReview} disabled={!!acting}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => submitDecision("reject")}
              disabled={!!acting}
              data-testid="admin-kyc-reject"
            >
              {acting === "reject" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <XCircle className="mr-2 h-4 w-4" />}
              Reject
            </Button>
            <Button
              type="button"
              onClick={() => submitDecision("approve")}
              disabled={!!acting}
              data-testid="admin-kyc-approve"
            >
              {acting === "approve" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default B2CKYCQueue;
